/* eslint-disable max-lines */
import React from 'react';

import { ExternalLink } from '../../../components/Clickables/ExternalLink/ExternalLink';
import { InternalLink } from '../../../components/Clickables/InternalLink/InternalLink';
import { Heading } from '../../../components/Heading/Heading';

interface IProps {
  appView?: boolean;
}

const PrivacyPolicy = ({ appView = true }: IProps) => (
  <div className="text-justify leading-6">
    <Heading as="h1" className="mb-4">
      Privacy policy
    </Heading>
    <p>Last updated: March 5th, 2024</p>
    <p>
      Previous version: <InternalLink to="/policies/privacy/3.0">August 1, 2023</InternalLink>
    </p>

    <Heading variant="sm" className="mb-4">
      1. About this Policy
    </Heading>

    <p>
      <span>
        This Privacy Policy together with the Regional consumer privacy notice for U.S. residents
        sets out how Fishbrain AB, Reg. No 556796-5370, a company duly incorporated and organized
        under the laws of Sweden, having its registered address at Sveav&auml;gen 15, 111 57
        Stockholm, Sweden (the &ldquo;Fishbrain Sites Entity&rdquo;),{' '}
      </span>
      <span>
        and Fishbrain Inc., a Delaware corporation having a place of business at 55 Shuman Blvd.,
        Suite 850, Naperville, IL, 60563 (the &ldquo;Fishbrain Shop Entity&rdquo; and, collectively
        with the Fishbrain Sites Entity, &ldquo;Fishbrain&rdquo;)
      </span>
      <span>&nbsp;collect, use and share </span>
      <span>personal data</span>
      <span>
        &nbsp;that we obtain as a result of your use of the Fishbrain mobile application (or
        &ldquo;app&rdquo;), our website at{' '}
      </span>
      <span>
        <ExternalLink disabled={appView} href="https://fishbrain.com">
          https://fishbrain.com
        </ExternalLink>
      </span>
      <span>
        &nbsp;(collectively with our apps and websites, the &ldquo;Sites&rdquo;), our platform to
        sell fishing-related goods and services (the &ldquo;Fishbrain Shop&rdquo;), and our other
        applications, software, features, tools, and services (all of the foregoing, collectively,
        the &ldquo;Services&rdquo;). As used herein, &lsquo;Fishbrain&rsquo;, &lsquo;we,&rsquo; and
        &lsquo;us&rsquo; refers to{' '}
      </span>
      <span>Fishbrain AB and/or Fishbrain Inc.</span>
      <span>, as applicable.</span>
    </p>
    <p>
      <span>
        By using or accessing the Services, you confirm that you have received this Privacy Policy
        and that you agree to the collection, use, and sharing of your personal data as explained in
        this Privacy Policy. If you do not agree, please do not use or access the Services.{' '}
      </span>
      <span>
        If you have any questions regarding the processing of your personal data, please contact us
      </span>
      <span>&nbsp;at </span>
      <span>
        <a href="mailto:support@fishbrain.com">support@fishbrain.com</a>
      </span>
      <span>.</span>
    </p>
    <Heading variant="sm" className="mb-4">
      <span>2</span>
      <span>. Your Data Protection Rights at Fishbrain </span>
    </Heading>
    <p>
      <span>
        You have several rights in relation to the personal data that we hold about you. If you want
        to exercise any of your rights described below, you can contact us at{' '}
      </span>
      <span>
        <a href="mailto:support@fishbrain.com">support@fishbrain.com</a>
      </span>
      <span>.</span>
      <span>
        &nbsp;We will try to respond to any request as soon as possible, but we will always do so
        within 30 days of receipt of your request and sufficient evidence of ID. Your rights
        include:
      </span>
    </p>
    <p>
      <span>
        <strong>Right of access:</strong>
      </span>
      <span>&nbsp;</span>
      <span>
        You have a right to know what information we hold about you and to have the information
        communicated to you. In case you want to exercise this right, please contact us at{' '}
      </span>
      <span>
        <a href="mailto:support@fishbrain.com">support@fishbrain.com</a>
      </span>
      <span>
        &nbsp;and let us know what information you would like to receive. We may need to verify your
        identity and/or require additional information before we provide you with any information or
        update your information as discussed below. Note that we may not be able to provide all the
        information you ask for or update your personal data, including if the information includes
        personal data about another person or if the request is unlawful.
      </span>
    </p>
    <p>
      <span>
        Examples of information you can access and edit easily at Fishbrain.com and/or in our app
        and/or in the Fishbrain Shop include, where applicable, current information regarding recent
        orders, personally identifiable information (including name, email, password, communications
        preferences, address book), payment settings (including promotional certificate and gift
        card balances), email notification settings, recommendations, shopping lists, gift
        registries, and wish lists, and your Profile.
      </span>
    </p>
    <p>
      <span>
        <strong>Right to restrict processing:</strong>
      </span>
      <span>
        &nbsp;You have the right to request that we stop processing all or some of your personal
        data, if you believe that your personal data is incorrect, that our processing is unlawful
        or that we do not need your personal data for a specific purpose.
      </span>
      <span>&nbsp;In case you want to exercise this right, please contact us at </span>
      <span>
        <a href="mailto:support@fishbrain.com">support@fishbrain.com</a>
      </span>
      <span>.</span>
      <span>&nbsp;</span>
    </p>
    <p>
      <span>
        <strong>Right to object:</strong>
      </span>
      <span>
        &nbsp;You have the right to object to our processing of your personal data, if the
        processing is based on our legitimate interest, and/or if we use your data for direct
        marketing purposes.
      </span>
      <span>
        &nbsp;If there are types of information you do not agree with us processing, or if you have
        any questions, you can contact us at{' '}
      </span>
      <span>
        <a href="mailto:support@fishbrain.com">support@fishbrain.com</a>
      </span>
      <span>.</span>
    </p>
    <p>
      <span>
        <strong>Revoking permissions:</strong>
      </span>
      <span>
        &nbsp;In some cases we will ask for consent to use your information for specific purposes.
        If you let us use your information, you can later change your mind and revoke your
        permission by changing the settings on your device if your device offers those options. If
        you do that, certain services may lose full functionality.
      </span>
    </p>
    <p>
      <span>
        <strong>Data Portability:</strong>
      </span>
      <span>
        &nbsp;You have the right to receive your personal data in a portable and machine-readable
        format.
      </span>
    </p>
    <p>
      <span>
        <strong>Data rectification/deletion:</strong>
      </span>
      <span>
        &nbsp;You have a right to have some of the personal data that we hold about you rectified or
        deleted. You can access, edit, add/delete, and/or update most of your basic account
        information in our app and/or in the Fishbrain Shop. Should you wish to have any information
        about you rectified or deleted, please contact us at{' '}
      </span>
      <span>
        <a href="mailto:support@fishbrain.com">support@fishbrain.com</a>
      </span>
      <span>.</span>
      <span>
        &nbsp;When we delete personal data about you, we may still retain some or all of that
        information for other purposes such as maintaining financial records, protecting or
        enforcing legal rights, or for technical reasons such as maintaining our database integrity.
        We may also retain your information in an anonymized form.
      </span>
    </p>
    <p>
      <span>
        <strong>Account deactivation/deletion:</strong>
      </span>
      <span>
        &nbsp;If you have an account with us and object to our processing of your personal data or
        you want us to deactivate your account, you can ask us to deactivate it by contacting us at{' '}
      </span>
      <span>
        <a href="mailto:support@fishbrain.com">support@fishbrain.com</a>
      </span>
      <span>
        . We may ask you for other information before we are able to deactivate your account. You
        can delete your account at any time by contacting us at{' '}
      </span>
      <span>
        <a href="mailto:support@fishbrain.com">support@fishbrain.com</a>
      </span>
      <span>.</span>
    </p>
    <p>
      <span>
        <strong>Withdrawal of consent:</strong>
      </span>
      <span>
        &nbsp;If you have selected to receive direct marketing communications from us, and you want
        to stop receiving them, you can click on the opt-out link in the marketing communication
        sent. If you do not want to receive email or other mail from us, please contact us at{' '}
      </span>
      <span>
        <a href="mailto:support@fishbrain.com">support@fishbrain.com</a>
      </span>
      <span>&nbsp;to adjust your customer communications preferences.</span>
    </p>
    <p>
      <span>
        <strong>Communicating with other Fishbrain users:</strong>
      </span>
      <span>
        &nbsp;It is important to us that you stay in control over whom you communicate with. Our
        Settings tool lets you indicate, among other things, whether you&rsquo;d like to receive
        messages from all Fishbrain users or just the Fishbrain users you follow, and whether you
        would like to block another Fishbrain user from contacting you again.
      </span>
    </p>
    <p>
      <span>
        <strong>
          Users in the European Union (EU), the European Economic Area (EEA) or the United Kingdom
          (UK):
        </strong>
      </span>
      <span>
        &nbsp;If you want to read more about your data protection rights &ndash; please see{' '}
      </span>
      <span>
        <ExternalLink href="https://www.imy.se/privatperson/dataskydd/dina-rattigheter/">
          here
        </ExternalLink>{' '}
      </span>
      <span>.</span>
    </p>
    <p>
      <span>
        As discussed above, you can always choose not to provide information, even though it might
        be needed to make a purchase or to take advantage of certain features of the Services.
      </span>
    </p>
    <Heading variant="sm" className="mb-4">
      <span>3. Collection of Personal Data</span>
    </Heading>
    <p>
      <span>
        Fishbrain&#39;s Services are for logging, tracking, and sharing fishing-related information,
        buying and rating fishing-related products through the Fishbrain Shop, and/or such other
        Services as we may make available from time to time. To provide these Services we collect,
        use, and store information about you.
      </span>
    </p>
    <p>
      <span>
        In this section, we set out the types of personal data that we collect when you interact
        with and use our Services. In section 4 below, we describe the purposes for which we use and
        for how long we store the below mentioned types of personal data. We collect personal data
        such as:
      </span>
    </p>
    <ul className="list-decimal pl-4">
      <li className="text-sm">
        <span>
          <strong>Contact and Identification Information</strong>
        </span>
        <span>&nbsp;</span>
        <span>-</span>
        <span>
          &nbsp;Name, username, password, e-mail address, telephone number, address, date of birth,
          profile photo, country, any other account or profile/settings information, any other user
          generated content that may be personally identifiable, such as photos and videos. You
          might also provide information about people to whom Fishbrain Shop purchases have been
          shipped, including addresses and phone numbers, and email addresses of your friends and
          other people.
        </span>
      </li>
      <li className="text-sm">
        <span>
          <strong>Camera and Photos - </strong>
        </span>
        <span>
          Some of our Services require us to collect images and other information from your
          device&rsquo;s camera and photos. For example, in order to upload photos from your camera
          roll, we need to access your camera and/or photos.
        </span>
      </li>
      <li className="text-sm">
        <span>
          <strong>Content Information</strong>
        </span>
        <span>&nbsp;</span>
        <span>- </span>
        <span>
          We collect content you create on our Services and information about the content you create
          or provide, such as if the recipient has viewed the content and the metadata that is
          provided with the content.
        </span>
      </li>
      <li className="text-sm">
        <span>
          <strong>Usage Information</strong>
        </span>
        <span>&nbsp;</span>
        <span>-</span>
        <span>
          &nbsp;Information such as how you interact with our Services and communicate with other
          Fishbrain users or participate in discussion boards or other community features, including
          time, date and number of interactions, access times, pages viewed/content browsed, and{' '}
        </span>
        <span>pages you visited before or after navigating to our Sites</span>
        <span>
          . This includes any information you volunteer when you contact customer support or
          communicate with us by phone, email, or otherwise.{' '}
        </span>
        <span>
          You further provide information when you search for a product or view a product, compile
          wish lists or other gift registries on the Fishbrain Shop, provide and rate reviews, and
          employ product availability alerts, if and as applicable.
        </span>
        <span>
          &nbsp;We may aggregate purchase information with that of other customers to create
          features like &lsquo;top sellers&rsquo;.{' '}
        </span>
        <span>
          When you interact with our Services, you further give us information about which services
          and the different features of these services you have used and how you have used them.
        </span>
      </li>
      <li className="text-sm">
        <span>
          <strong>Device Information</strong>
        </span>
        <span>&nbsp;</span>
        <span>-</span>
        <span>
          &nbsp;Device identification, hardware model, operating system version, platform, device
          memory,{' '}
        </span>
        <span>unique device identifiers</span>
        <span>
          , IP address, browser and plug-in type and version, language, time zone, information from
          device sensors s
        </span>
        <span>
          uch as accelerometers, gyroscopes, compasses, microphones, and whether you have headphones
          connected. You further give us information about your wireless and mobile network
          connections, such as mobile phone number, service provider, and signal strength.
        </span>
        <span>&nbsp;</span>
      </li>
      <li className="text-sm">
        <span>
          <strong>Device Phonebook - </strong>
        </span>
        <span>
          Because we enable you to communicate with friends, we may &mdash; with your permission
          &mdash; collect information from your device&rsquo;s phonebook.
        </span>
      </li>
      <li className="text-sm">
        <span>
          <strong>Location Information</strong>
        </span>
        <span>&nbsp;</span>
        <span>-</span>
        <span>&nbsp;</span>
        <span>
          When you use our Services we may collect information about your location. With your
          permission, we may also collect information about your precise location using methods that
          include GPS, wireless networks, cell towers, Wi-Fi access points, and other sensors, such
          as gyroscopes, accelerometers, and compasses.{' '}
        </span>
        <span>
          If you have given us consent to collect your location information, we will collect that
          information each time you open our app in order to improve your experience of our
          Services, for example to show you posts and catches that are relevant to you, based on
          where you are located on the map. You can read more about this in Section 4 below.{' '}
        </span>
        <span>
          Most mobile devices allow you to turn off location services; if you have questions about
          how to do this, we recommend you contact your mobile service carrier or your device
          manufacturer.
        </span>
      </li>
      <li className="text-sm">
        <span>
          <strong>Financial Information -</strong>
        </span>
        <span>
          &nbsp;When you place an order through the Fishbrain Shop, you provide information
          including purchases and credit card or debit card number
        </span>
        <span>.</span>
        <span>&nbsp;You can read more about this in Section 7 below.</span>
      </li>
      <li className="text-sm">
        <span>
          <strong>Session Information -</strong>
        </span>
        <span>
          &nbsp;We may measure and collect session information with respect to our website and apps,
          including, as applicable, page response times, download errors, length of visits to
          certain pages, page interaction information (such as scrolling, clicks, and mouse-overs),
          methods used to browse away from the page, and length and frequency of use of the app.
        </span>
      </li>
      <li className="text-sm">
        <span>
          <strong>Information Collected by Cookies and Other Technologies</strong>
        </span>
        <span>&nbsp;</span>
        <span>-</span>
        <span>
          &nbsp;Like most online services and mobile applications, we may use cookies and other
          technologies, such as web beacons, software development kits, and web storage and other
          identifiers to collect information about your activity, browser, and device. We may also
          use these technologies to collect information when you interact with services we offer
          through one of our partners, such as commerce features.{' '}
        </span>
        <span>Most web browsers are set to accept cookies by default.</span>
        <span>
          &nbsp;If you prefer, you can usually remove or reject browser cookies through the settings
          on your browser or device, however removing or rejecting cookies could affect the
          availability and functionality of our Services.{' '}
        </span>
        <span>Section 9 </span>
        <span>below contains additional information about cookies.</span>
      </li>
      <li className="text-sm">
        <span>
          <strong>Information from Third Parties - </strong>
        </span>
        <span>
          If you link your Fishbrain account to another service (like a third-party app), we may
          receive information from the other service, like how you use that service. For instance,
          when you choose to sign up or log in to our platform using sign-in features provided by
          third parties, such third parties provide us with information, such as your name, email
          address, and public profile. Carriers and other third parties may provide updated delivery
          and address information, which we use to correct our records and deliver your next
          purchase or communication more easily. Further, merchants whose products we sell may
          provide us account information, purchase or redemption information, and page-view
          information. Advertisers may share information with us as well. We may use this
          information obtained by advertisers, among other ways, to help target or measure the
          performance of advertisements. You can read more about sharing of personal data with such
          parties in Section 5 below.
        </span>
      </li>
      <li className="text-sm">
        <span>
          <strong>Information from Other Users -</strong>
        </span>
        <span>
          &nbsp;If another user uploads their contact list, we may combine information from that
          user&rsquo;s contact list with other information we have collected about you.
        </span>
      </li>
    </ul>
    <Heading variant="sm" className="mb-4">
      <span>4</span>
      <span>. Use of Personal Data </span>
    </Heading>
    <p>
      <span>
        Your privacy is important to us. Where the GDPR is applicable, we will only process your
        personal data if we have a legal basis for the{' '}
      </span>
      <span>processing</span>
      <span>. </span>
    </p>
    <p>
      <span>In the tables below, we set out:</span>
    </p>
    <ol className="list-decimal pl-4">
      <li className="text-sm">
        <span>The purpose for which we use your personal data,</span>
      </li>
      <li className="text-sm">
        <span>
          What legal basis we have to process the personal data about you, under current data
          protection legislation (such as the GDPR), and
        </span>
      </li>
      <li className="text-sm">
        <span>Which types of personal data we use for that purpose,</span>
      </li>
      <li className="text-sm">
        <span>
          When we stop using the personal data for each purpose (retention schedule). You can read
          more about data retention in Section 8 below.
        </span>
      </li>
    </ol>
    <div className="overflow-x-auto">
      <table className="my-6 border">
        <thead>
          <tr>
            <th scope="col" className="border p-3 text-left">
              <p>
                <span>Purpose of processing</span>
              </p>
            </th>
            <th scope="col" className="border p-3 text-left">
              <p>
                <span>Legal basis for processing</span>
              </p>
            </th>
            <th scope="col" className="border p-3 text-left">
              <p>
                <span>Types of personal data used for the purpose </span>
              </p>
            </th>
            <th scope="col" className="border p-3 text-left">
              <p>
                <span>For how long we process personal data for the purpose </span>
              </p>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="border p-3 text-left">
              <p>
                <span>
                  To administer the purchase when you place an order through the Fishbrain Shop.
                </span>
              </p>
            </td>
            <td className="border p-3 text-left">
              <p>
                <span>Contractual necessity (Article 6(1)(b) GDPR).</span>
              </p>
            </td>
            <td className="border p-3 text-left">
              <ul className="list-disc pl-4">
                <li className="text-sm">
                  <span>Contact and identification information</span>
                </li>
              </ul>
            </td>
            <td className="border p-3 text-left">
              <p>
                <span>
                  For as long as necessary in order to fulfill the contract and &nbsp;provide our
                  Services to you.
                </span>
              </p>
            </td>
          </tr>
          <tr>
            <td className="border p-3 text-left">
              <p>
                <span>To enable in-app purchases and/or subscriptions.</span>
              </p>
            </td>
            <td className="border p-3 text-left">
              <p>
                <span>Contractual necessity (Article 6(1)(b) GDPR).</span>
              </p>
            </td>
            <td className="border p-3 text-left">
              <ul className="list-disc pl-4">
                <li className="text-sm">
                  <span>Contact and identification information</span>
                </li>
                <li className="text-sm">
                  <span>Usage information</span>
                </li>
                <li className="text-sm">
                  <span>Device information</span>
                </li>
              </ul>
            </td>
            <td className="border p-3 text-left">
              <p>
                <span>
                  For as long as you have a subscription at Fishbrain and/or for as long as
                  necessary in order to provide our Services to you.
                </span>
              </p>
            </td>
          </tr>
          <tr>
            <td className="border p-3 text-left">
              <p>
                <span>
                  To monitor and analyze trends and usage to develop, operate, improve, deliver,
                  maintain, enhance and protect our Services.
                </span>
              </p>
            </td>
            <td className="border p-3 text-left">
              <p>
                <span>
                  Legitimate interest in providing and improving our Services (Article 6(1)(f)
                  GDPR).
                </span>
              </p>
            </td>
            <td className="border p-3 text-left">
              <ul className="list-disc pl-4">
                <li className="text-sm">
                  <span>Contact and identification information</span>
                </li>
                <li className="text-sm">
                  <span>Location information</span>
                </li>
                <li className="text-sm">
                  <span>Device information</span>
                </li>
                <li className="text-sm">
                  <span>Usage information</span>
                </li>
              </ul>
            </td>
            <td className="border p-3 text-left">
              <p>
                <span>
                  For as long as necessary in order to provide our Services to you and/or for as
                  long we keep your personal data in our systems in accordance with this Privacy
                  Policy. See Section 8 for more information on data retention and deletion of your
                  personal data.
                </span>
              </p>
            </td>
          </tr>
          <tr>
            <td className="border p-3 text-left">
              <p>
                <span>
                  To engage with you as a customer, including personalizing our Services by, among
                  other things, suggesting users to follow or profile information, or customizing
                  the content we show you (including using your precise location information if
                  you&rsquo;ve given us permission to collect that information).
                </span>
              </p>
            </td>
            <td className="border p-3 text-left">
              <p>
                <span>
                  Consent (Article 6(1)(a) GDPR) and/or legitimate interest in providing and
                  personalizing our Services, including helping you find friends and content we
                  think you will like (Article 6(1)(f) GDPR).
                </span>
              </p>
            </td>
            <td className="border p-3 text-left">
              <ul className="list-disc pl-4">
                <li className="text-sm">
                  <span>Contact and identification information </span>
                </li>
                <li className="text-sm">
                  <span>Location information </span>
                </li>
                <li className="text-sm">
                  <span>Usage information</span>
                </li>
                <li className="text-sm">
                  <span>Device information &nbsp;</span>
                </li>
              </ul>
            </td>
            <td className="border p-3 text-left">
              <p>
                <span>
                  For as long as your consent is not revoked and/or for as long as necessary in
                  order to provide our Services to you.
                </span>
              </p>
            </td>
          </tr>
          <tr>
            <td className="border p-3 text-left">
              <p>
                <span>
                  To analyze and respond to customer inquiries when contacting our Support team,
                  including to verify your identity when contacting us.
                </span>
              </p>
            </td>
            <td className="border p-3 text-left">
              <p>
                <span>
                  Legitimate interest in providing and improving our Services, including protecting
                  your account and providing customer support (Article 6(1)(f) GDPR).
                </span>
              </p>
            </td>
            <td className="border p-3 text-left">
              <ul className="list-disc pl-4">
                <li className="text-sm">
                  <span>Contact and identification information</span>
                </li>
                <li className="text-sm">
                  <span>Usage information</span>
                </li>
                <li className="text-sm">
                  <span>Session information</span>
                </li>
              </ul>
            </td>
            <td className="border p-3 text-left">
              <p>
                <span>For as long as necessary in order to provide our Services to you.</span>
              </p>
            </td>
          </tr>
          <tr>
            <td className="border p-3 text-left">
              <p>
                <span>
                  To analyze usage activities in order to optimize advertising campaigns and try to
                  show you ads you will find interesting, since some of our Services are free.
                </span>
              </p>
            </td>
            <td className="border p-3 text-left">
              <p>
                <span>Consent (Article 6(1)(a) GDPR) and/or </span>
                <span>
                  legitimate interest of optimizing advertisements to deliver relevant advertising
                  you will find relevant and interesting by understanding your interests and
                  preferences (Article 6(1)(f) GDPR).
                </span>
              </p>
            </td>
            <td className="border p-3 text-left">
              <ul className="list-disc pl-4">
                <li className="text-sm">
                  <span>Usage information</span>
                </li>
                <li className="text-sm">
                  <span>Device information</span>
                </li>
                <li className="text-sm">
                  <span>
                    Information collected by cookies and other technologies, including such
                    information analyzed by third parties.
                  </span>
                </li>
              </ul>
            </td>
            <td className="border p-3 text-left">
              <p>
                <span>
                  For as long as &nbsp;you use our Services or until you revoke your consent to
                  process information collected by cookies and other technologies.
                </span>
              </p>
            </td>
          </tr>
          <tr>
            <td className="border p-3 text-left">
              <p>
                <span>
                  To deliver push notifications and emails, or to share information about our
                  Services and promotional offers that we think may interest you (CRM-system). To
                  help us make emails more useful and interesting, we often receive confirmation
                  when you open an email from Fishbrain (if your computer supports such
                  capabilities). We also compare our customer list to lists received from other
                  companies, in an effort to avoid sending unnecessary messages.
                </span>
              </p>
            </td>
            <td className="border p-3 text-left">
              <p>
                <span>Consent (Article 6(1)(a) GDPR) and/o</span>
                <span>
                  r legitimate interest in providing and marketing our Services (Article 6(1)(f)
                  GDPR).
                </span>
              </p>
            </td>
            <td className="border p-3 text-left">
              <ul className="list-disc pl-4">
                <li className="text-sm">
                  <span>Contact and identification information</span>
                </li>
                <li className="text-sm">
                  <span>Location information</span>
                </li>
                <li className="text-sm">
                  <span>Device information, </span>
                </li>
                <li className="text-sm">
                  <span>Usage information</span>
                </li>
              </ul>
            </td>
            <td className="border p-3 text-left">
              <p>
                <span>
                  For as long as necessary in order to provide our Services to you, or until you
                  revoke your permission by clicking on the unsubscribe link or similar mechanism as
                  provided.
                </span>
              </p>
            </td>
          </tr>
          <tr>
            <td className="border p-3 text-left">
              <p>
                <span>
                  Provide and improve search results, including through the use of your precise
                  location information (if you&rsquo;ve given us permission to collect that
                  information), both on and off our Services.
                </span>
              </p>
            </td>
            <td className="border p-3 text-left">
              <p>
                <span>Consent (Article 6(1)(a) GDPR) and/or</span>
                <span>
                  &nbsp;legitimate interest in providing and improving our Services (Article 6(1)(f)
                  GDPR).
                </span>
              </p>
            </td>
            <td className="border p-3 text-left">
              <ul className="list-disc pl-4">
                <li className="text-sm">
                  <span>Contact and identification information</span>
                </li>
                <li className="text-sm">
                  <span>Location information</span>
                </li>
                <li className="text-sm">
                  <span>Device information</span>
                </li>
                <li className="text-sm">
                  <span>Usage information</span>
                </li>
              </ul>
            </td>
            <td className="border p-3 text-left">
              <p>
                <span>
                  For as long as necessary in order to provide our Services to you or until you
                  revoke your consent.
                </span>
              </p>
            </td>
          </tr>
          <tr>
            <td className="border p-3 text-left">
              <p>
                <span>
                  Contextualize your experience by, among other things, tagging your content using
                  your precise location information (if you&rsquo;ve given us permission to collect
                  that information), and applying labels based on the content
                </span>
                <span>.</span>
              </p>
            </td>
            <td className="border p-3 text-left">
              <p>
                <span>Consent (Article 6(1)(a) GDPR) and/or </span>
                <span>legitimate interest in providing our Services (Article 6(1)(f) GDPR).</span>
              </p>
            </td>
            <td className="border p-3 text-left">
              <ul className="list-disc pl-4">
                <li className="text-sm">
                  <span>Location information</span>
                </li>
                <li className="text-sm">
                  <span>Content information</span>
                </li>
              </ul>
            </td>
            <td className="border p-3 text-left">
              <p>
                <span>
                  For as long as necessary in order to provide our Services to you or until you
                  revoke your consent.
                </span>
              </p>
            </td>
          </tr>
          <tr>
            <td className="border p-3 text-left">
              <p>
                <span>
                  Enforce our Terms of Service and other usage policies and protect Fishbrain from
                  legal claims and safeguard Fishbrain&rsquo;s legal rights.
                </span>
              </p>
            </td>
            <td className="border p-3 text-left">
              <p>
                <span>
                  Legitimate interest in providing our Services, exercising our rights and
                  protecting ourselves from legal claims (Article 6(1)(f) GDPR).
                </span>
              </p>
            </td>
            <td className="border p-3 text-left">
              <p>
                <span>All types of personal data mentioned in Section 3 might be applicable.</span>
              </p>
            </td>
            <td className="border p-3 text-left">
              <p>
                <span>
                  For as long as you are using our Services and thereafter for a period up to 10
                  years based on statute of limitations.
                </span>
              </p>
            </td>
          </tr>
          <tr>
            <td className="border p-3 text-left">
              <p>
                <span>
                  To verify your identity and prevent fraud or other unauthorized or illegal
                  activity, and enhance the safety and security of our Services, including risk
                  management.
                </span>
              </p>
            </td>
            <td className="border p-3 text-left">
              <p>
                <span>
                  Legitimate interest in providing our Services and preventing any unauthorized or
                  illegal activity and securing our Services, &nbsp;including protecting your
                  account (Article 6(1)(f) GDPR).
                </span>
              </p>
            </td>
            <td className="border p-3 text-left">
              <ul className="list-disc pl-4">
                <li className="text-sm">
                  <span>Contact and identification information</span>
                </li>
                <li className="text-sm">
                  <span>Content information</span>
                </li>
                <li className="text-sm">
                  <span>Usage information</span>
                </li>
              </ul>
            </td>
            <td className="border p-3 text-left">
              <p>
                <span>
                  For as long as necessary in order to provide our Services to you and to prevent
                  any unauthorized or illegal activity.
                </span>
              </p>
            </td>
          </tr>
          <tr>
            <td className="border p-3 text-left">
              <p>
                <span>To fulfill legal obligations applicable to Fishbrain.</span>
              </p>
            </td>
            <td className="border p-3 text-left">
              <p>
                <span>Legal obligation by law to retain personal data (Article 6(1)(c) GDPR).</span>
              </p>
            </td>
            <td className="border p-3 text-left">
              <p>
                <span>All types of personal data mentioned in Section 3 might be applicable.</span>
              </p>
            </td>
            <td className="border p-3 text-left">
              <p>
                <span>
                  This processing takes place for the entire period during which Fishbrain must
                  retain the information, to comply with applicable law. As an example, under
                  anti-money laundering laws or bookkeeping laws, it is generally retained for 5 and
                  7 years, respectively.
                </span>
              </p>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <Heading variant="sm" className="mb-4">
      <span>5</span>
      <span>. Sharing of </span>
      <span>Personal Data</span>
    </Heading>
    <p>
      <span>
        We may share aggregated, non-personally identifiable, or de-identified information with
        third parties
      </span>
      <span>
        , and we will maintain the information in such form and not attempt to re-identify the
        information, except that we may attempt to re-identify the information solely for the
        purpose of determining whether de-identification processes used satisfy legal requirements.
        The third parties may be research partners providing valuable insights for conservation and
        sustainability efforts, but also service providers contractually obligated to use the data
        only for the specified service function. However, w
      </span>
      <span>e do not sell personal data about our users t</span>
      <span>o any unrelated third parties for any consideration. </span>
    </p>
    <p>
      <span>
        Where a third party processes Fishbrain user data on our behalf, it is subject to security
        and confidentiality obligations consistent with, and not less restrictive than, this Privacy
        Policy and applicable law, and it may not use the personal data for purposes other than to
        perform their functions. In other cases, we provide services or sell product lines jointly
        with these businesses. We share customer information related to those transactions with that
        third party.
      </span>
    </p>
    <p>
      <span>
        In the tables below we describe to whom and for which reasons we may share your personal
        data.
      </span>
    </p>
    <div className="overflow-x-auto">
      <table className="my-6 border">
        <thead>
          <tr>
            <th scope="col" className="border p-3 text-left">
              <p>
                <span>Categories of recipients</span>
              </p>
            </th>
            <th scope="col" className="border p-3 text-left">
              <p>
                <span>Categories of personal data</span>
              </p>
            </th>
            <th scope="col" className="border p-3 text-left">
              <p>
                <span>Reasons for sharing</span>
              </p>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="border p-3 text-left">
              <p>
                <span>Fishbrain Group Companies</span>
                <span>
                  , including Fishbrain AB, Fishbrain Inc., and other companies acquired by
                  Fishbrain (please see Section 15 below)
                </span>
              </p>
            </td>
            <td className="border p-3 text-left">
              <p>
                <span>
                  All types of personal data mentioned in Section 3 might be applicable and thus be
                  shared, or limited remote access may be given to the relevant company in our
                  Group.
                </span>
              </p>
            </td>
            <td className="border p-3 text-left">
              <p>
                <span>
                  In order to provide and improve our Services. The Fishbrain Group Companies are
                  either subject to this Privacy Policy or follow practices at least as protective
                  as those described in this Privacy Policy.
                </span>
              </p>
            </td>
          </tr>
          <tr>
            <td className="border p-3 text-left">
              <p>
                <span>Sellers and Carriers on the Fishbrain Shop</span>
              </p>
            </td>
            <td className="border p-3 text-left">
              <ul className="list-disc pl-4">
                <li className="text-sm">
                  <span>
                    Contact and identification information, such as name, address, email address,
                    and telephone number.
                  </span>
                </li>
              </ul>
            </td>
            <td className="border p-3 text-left">
              <p>
                <span>
                  In order to fulfill their transactions with you. All such sellers are required to
                  comply with this Privacy Policy (or more restrictive terms) and additional
                  restrictions with respect to such information; however they may share such
                  information with companies and individuals that perform functions on their behalf,
                  such as fulfilling orders and delivering packages. You acknowledge and agree that
                  we are not responsible for how such sellers use your information.
                </span>
              </p>
            </td>
          </tr>
          <tr>
            <td className="border p-3 text-left">
              <p>
                <span>Service providers and business partners. </span>
              </p>
              <p>
                <span>
                  We engage service providers that help us provide, support, and develop our
                  Services and understand how it is used. For instance, we work with Amazon Web
                  Services to host and process our data and we work with various analytics providers
                  to help us understand the use of our Services to build a better product. Other
                  examples of functions performed on our behalf include fulfilling orders,
                  delivering packages, sending postal mail and email, removing repetitive
                  information from customer lists, providing marketing assistance, providing search
                  results and links (including paid listings and links), processing credit card
                  payments, and providing customer service.
                </span>
              </p>
            </td>
            <td className="border p-3 text-left">
              <p>
                <span>
                  The type of personal data shared depends on the applicable service. We are
                  committed to only sharing what is necessary for the performance of the specified
                  function.
                </span>
              </p>
              <ul className="list-disc pl-4">
                <li className="text-sm">
                  <span>Contact and identification information.</span>
                </li>
              </ul>
              <ul className="list-disc pl-4">
                <li className="text-sm">
                  <span>Location information</span>
                </li>
                <li className="text-sm">
                  <span>Usage information </span>
                </li>
                <li className="text-sm">
                  <span>Device information</span>
                </li>
                <li className="text-sm">
                  <span>Session information</span>
                </li>
              </ul>
            </td>
            <td className="border p-3 text-left">
              <p>
                <span>
                  In order to perform functions on our behalf and process user data in order to help
                  us provide and improve our Services. Such recipients are contractually obligated
                  to use the data only for the specified function.
                </span>
              </p>
            </td>
          </tr>
          <tr>
            <td className="border p-3 text-left">
              <p>
                <span>Advertising partners.</span>
                <span>
                  &nbsp;You may see advertisements when you are on other websites and/or apps, such
                  as on social media.
                </span>
              </p>
            </td>
            <td className="border p-3 text-left">
              <p>
                <span>
                  When you are interacting with such ads, our advertising partners may collect data,
                  such as:
                </span>
              </p>
              <ul className="list-disc pl-4">
                <li className="text-sm">
                  <span>Cookies and Other Technologies</span>
                </li>
                <li className="text-sm">
                  <span>Usage information </span>
                </li>
                <li className="text-sm">
                  <span>Device information, such as IP addresses</span>
                </li>
              </ul>
              <p>
                <span>You can read more about this in Section 6 below.</span>
              </p>
            </td>
            <td className="border p-3 text-left">
              <p>
                <span>
                  To personalize offers or advertisements, analytics about how you engage with
                  websites or ads and other commercial purposes, such as to present offers,
                  promotions or other marketing that we think will be relevant to you.
                </span>
              </p>
            </td>
          </tr>
          <tr>
            <td className="border p-3 text-left">
              <p>
                <span>Corporate Transactions.</span>
                <span>
                  &nbsp;Your information may be disclosed to third parties in the event of a
                  corporate transaction, such as a merger, sale of assets or shares, reorganization,
                  financing, change of control, liquidation or bankruptcy, or acquisition of all or
                  a portion of our business to another company.
                </span>
              </p>
            </td>
            <td className="border p-3 text-left">
              <p>
                <span>All types of personal data mentioned in Section 3 might be applicable.</span>
              </p>
            </td>
            <td className="border p-3 text-left">
              <p>
                <span>
                  To complete such corporate transactions, before, during and after the transaction
                  closes. Please note that such third parties will follow practices at least as
                  protective as those described in this Privacy Policy, and will be bound by
                  requirements of confidentiality.
                </span>
              </p>
            </td>
          </tr>
          <tr>
            <td className="border p-3 text-left">
              <p>
                <span>Law enforcement authorities </span>
                <span>
                  and other authorities, other parties to a dispute, or other companies and
                  organizations for fraud protection and credit risk reduction.
                </span>
              </p>
            </td>
            <td className="border p-3 text-left">
              <p>
                <span>
                  All types of personal data mentioned in Section 3 might be applicable. However,
                  please note that we will only share data to the extent required by such parties.
                </span>
              </p>
            </td>
            <td className="border p-3 text-left">
              <p>
                <span>
                  To comply with any court order, law or legal process, including to respond to any
                  government or regulatory request; or applicable law, rule or regulation; or to
                  investigate, remedy or enforce potential Terms of Service violations; protects the
                  rights, property and safety of Fishbrain, our users or others; or to detect and
                  resolve any fraud or security concerns. This includes exchanging information with
                  other companies and organizations for fraud protection and credit risk reduction.
                </span>
              </p>
            </td>
          </tr>
          <tr>
            <td className="border p-3 text-left">
              <p>
                <span>Other Fishbrain members</span>
              </p>
            </td>
            <td className="border p-3 text-left">
              <p>
                <span>
                  Username, name, and profile photo, information about how you have interacted with
                  our Services, the names of Fishbrain members you follow and/or we suggest that you
                  follow, and other information that will help Fishbrain members understand your
                  connections with others using our Service, for example whether you have interests
                  in common, such as the type(s) of fishing you are most interested in, any
                  additional information you have directed us to share, and content you post or
                  send.
                </span>
              </p>
            </td>
            <td className="border p-3 text-left">
              <p>
                <span>
                  In order to provide, contextualize and improve your user experience using our
                  Services. How widely your content is shared depends on your personal settings and
                  the type of service you are using.
                </span>
              </p>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <p>
      <span>
        <strong>Sharing with all Fishbrain Members and the General Public</strong>
      </span>
    </p>
    <p>
      <span>
        We may share public information like your name, username, and profile pictures without your
        permission. Any content that you submit to an inherently public service, including your
        postings and comments on Fishbrain, may be viewed and shared by the public at large both on
        and off our Services, including through search results, on websites, in apps, including
        social networks, and in online and offline broadcasts.
      </span>
    </p>
    <p>
      <span>
        When you submit information as part of a competition or interact with us on social media, we
        may publish the information you submitted with your name in our app and social media
        channels.
      </span>
    </p>
    <p>
      <span>
        By using our service, you acknowledge and agree that your personal data may be hosted and/or
        transferred to such recipients both within and outside the European Union, the European
        Economic Area and the United Kingdom. Any personal data collected from you may only be
        transferred outside the European Union, the European Economic Area and the United Kingdom by
        observing applicable privacy regulations and ensuring your privacy rights are protected. If
        you are located in the European Union, the European Economic Area or the United Kingdom, you
        can read more about this under Section 11 below.
        <br />
        <br />
        Other than as set out above, you will receive notice when information about you might go to
        third parties, and you will have an opportunity to choose not to share the information.
      </span>
    </p>
    <Heading variant="sm" className="mb-4">
      <span>6</span>
      <span>. Third-Party Content and Integrations</span>
    </Heading>
    <p>
      <span>
        Our services may contain third-party links and search results, include third-party
        integrations, or offer a co-branded or third-party-branded service. Through these links,
        third-party integrations, and co-branded or third-party-branded services, you may be
        providing information (including personal data) directly to the third party, us, or both.
      </span>
    </p>
    <p>
      <span>
        As always, we encourage you to review the privacy policies of every third-party service that
        you visit or use, including those third parties you interact with through our services.
      </span>
    </p>
    <p>
      <span>
        Sometimes we send offers to selected groups of Fishbrain users on behalf of other
        businesses. When we do this, we do not share any of your information with that business. If
        you do not want to receive such offers, please contact{' '}
      </span>
      <span>
        <a href="mailto:support@fishbrain.com">support@fishbrain.com</a>
      </span>
      <span>. </span>
    </p>
    <p>
      <span>Fishbrain may embed YouTube-videos through</span>
      <span>
        <a href="https://developers.google.com/youtube/terms/developer-policies#definition-youtube-api-services">
          YouTube API Services
        </a>
      </span>
      <span> and in such cases this integration is governed by the</span>
      <span>
        <a href="https://www.youtube.com/t/terms">YouTube terms of service</a>
      </span>
      <span>. </span>
    </p>
    <Heading variant="sm" className="mb-4">
      <span>7</span>
      <span>. Information Security</span>
    </Heading>
    <ul className="list-disc pl-4">
      <li className="text-sm">
        <span>
          We work to protect the security of your information during transmission by using Secure
          Sockets Layer (SSL) software, which encrypts information you input. Please note that we
          cannot be held liable for any loss of or disclosure of data or any losses or damages
          incurred due to errors in transmission or unauthorized or unlawful acts of third parties
          or your decision to disclose your personal data.
        </span>
      </li>
      <li className="text-sm">
        <span>
          We use one or more third party payment processors to process payments. In connection with
          the processing of such payments, we do not retain any personally identifiable information
          or any financial information such as credit card numbers. Rather, all such information is
          provided directly to our third-party processors, Stripe and Braintree, whose use of your
          personal data is governed by their privacy policy, which may be viewed at{' '}
        </span>
        <ExternalLink href="https://stripe.com/us/privacy">
          https://stripe.com/us/privacy
        </ExternalLink>{' '}
        and{' '}
        <ExternalLink href="https://www.braintreepayments.com/legal/braintree-privacy-policy">
          https://www.braintreepayments.com/legal/braintree-privacy-policy
        </ExternalLink>
        , respectively.
      </li>
      <li className="text-sm">
        <span>
          It is important for you to protect against unauthorized access to your password and to
          your computer. Be sure to sign off when finished using a shared device.
        </span>
      </li>
    </ul>
    <Heading variant="sm" className="mb-4">
      <span>8</span>
      <span>. Data Retention</span>
    </Heading>
    <p>
      <span>
        We store profile/account data like name, username, password, email address, telephone
        number, address, and the content created by you such as posts, photos, and videos (and
        associated location information), as long as it is necessary for the provision of our
        Services to you. If you wish us to discontinue storing this data, please contact us at{' '}
      </span>
      <span>
        <a href="mailto:support@fishbrain.com">support@fishbrain.com</a>
      </span>
      <span>. </span>
    </p>
    <p>
      <span>
        Keep in mind that we cannot promise that deletion will occur within a specific time frame.
        There may be legal requirements to store your personal data, and we may receive a legal
        process asking us to keep content, if we receive reports of abuse or other Terms of Service
        violations, or if your account is flagged by others or our systems for abuse or other Terms
        of Service violations.
      </span>
    </p>
    <p>
      <span>
        If you decide to stop using Fishbrain, you can request that we delete your account.
      </span>
    </p>
    <p>
      <span>
        While our systems are designed to carry out our deletion practices automatically, we cannot
        promise that deletion will occur within a specific timeframe. There may be legal
        requirements to store your data and we may need to suspend those deletion practices if we
        receive valid legal process asking us to preserve content or if we receive reports of abuse
        or other Terms of Service violations. We may also retain certain information in backup for a
        limited period of time or as required by law.
      </span>
    </p>
    <Heading variant="sm" className="mb-4">
      <span>9</span>
      <span>. Cookies</span>
    </Heading>
    <p>
      <span>When using the Services you </span>
      <span>may</span>
      <span>
        &nbsp;agree to our use of cookies on the Services. Cookies are small text files that
        websites send to your device to enable us to recognize your device, provide you with a
        personalized experience on our Sites or otherwise through the Services, and uniquely
        identify your browser, store information, and settings in your browser, to operate the
        shopping cart for Sellers, to provide reporting across multiple sessions and devices, and
        provide certain personalized features of our Services.
      </span>
    </p>
    <p>
      <span>
        If you agree to the use of cookies on the Services, we may also let other companies use
        cookies, web beacons, and similar technologies on our Services. These companies may collect
        information about how you use our Services over time and combine it with similar information
        from other services and companies. This information may be used to, among other things,
        analyze and track data, determine the popularity of certain content, and better understand
        your online activity. You acknowledge and agree that we are not responsible for how those
        third parties collect or use your information. We may also collect information about your
        activity on third-party services that use cookies and other technologies provided by us.
      </span>
    </p>
    <p>
      <span>Read more about how we use cookies on our Sites in our Cookie Policy - </span>
      <span>
        <ExternalLink disabled={appView} href="https://fishbrain.com/cookie-policy">
          https://fishbrain.com/cookie-policy
        </ExternalLink>
      </span>
      <span>.</span>
    </p>
    <Heading variant="sm" className="mb-4">
      <span>10. Children</span>
    </Heading>
    <p>
      <span>
        Our services are not intended for, and we do not direct them to, anyone under 13. We do not
        knowingly collect personal data from anyone under 13.
      </span>
    </p>
    <p>
      <span>
        We do not sell products for purchase by children. We may sell children&#39;s products for
        purchase by adults. If you are under 18, you may use our Services to purchase products only
        with the consent and involvement of a parent or guardian.
      </span>
    </p>
    <p>
      <span>
        If you are under the age of 13, please do not submit any personal data through the Services.
        We encourage parents and legal guardians to monitor their children&#39;s Internet usage and
        to help enforce our Privacy Policy by instructing their children never to provide personal
        data on the Services. If you have reason to believe that a child under the age of 13 has
        provided personal data to Fishbrain through the Services, please contact us at{' '}
      </span>
      <span>
        <a href="mailto:support@fishbrain.com">support@fishbrain.com</a>
      </span>
      <span>, and we will endeavor to delete that information from our databases.</span>
    </p>
    <Heading variant="sm" className="mb-4">
      <span>12. </span>
      <span>Users in the </span>
      <span>United states</span>
    </Heading>
    <p>
      <span>
        <br />
        In addition to the rest of the privacy policy, the following policies apply solely to
        residents of the U.S with respect to personal information Company processes as a business.
      </span>
    </p>
    <p>
      {' '}
      {appView ? (
        <InternalLink to="/policies/privacy/regional">
          Regional consumer privacy notice for U.S. residents
        </InternalLink>
      ) : (
        <InternalLink to="/privacy/regional">
          Regional consumer privacy notice for U.S. residents
        </InternalLink>
      )}
    </p>
    <Heading variant="sm" className="mb-4">
      <span>13. Users in the </span>
      <span>European Union, the European Economic Area and the United Kingdom</span>
    </Heading>
    <p>
      <span>
        If you&rsquo;re a user located in the European Union (EU), the European Economic Area (EEA)
        or the United Kingdom (UK), you should know that Fishbrain AB is the controller of your
        personal data and you have several rights under the General Data Protection Regulation with
        respect to your personal data. Here is some additional information we would like to bring to
        your attention:
      </span>
    </p>
    <ol className="list-decimal pl-4">
      <li className="text-sm">
        <span>Transfers</span>
        <span>. I</span>
        <span>
          n certain situations, such as when we share your information within the Fishbrain Group, a
          service provider, a business partner or subcontractor which operates outside of the
          EU/EEA, we may collect your personal data from,{' '}
        </span>
        <span>transfer it to, and/or store and process it outside of the EU/EEA</span>
        <span>, including in the US</span>
        <span>
          . Whenever Fishbrain transfers information of users within the EU/EEA outside of the
          EU/EEA, we take appropriate safeguards to ensure that the same level of protection applies
          to your personal data, according to the applicable privacy legislation in the respective
          countries. Your rights in respect to your personal data (described in Section 2), are not
          affected when data is transferred outside of the EU/EEA.
        </span>
        <p>
          <span>
            When transferring personal data to countries outside of the EU/EEA, these safeguards
            include, but are not limited to, ensuring implementation of the European
            Commission&rsquo;s Standard Contractual Clauses with applicable group companies, service
            providers and/or others. If the European Commission has decided that the country to
            which your personal data is being transferred has an adequate level of protection, we
            rely on that adequacy decision. You can read more about Standard Contractual Clauses and
            about which countries are deemed to have an adequate level of protection at{' '}
          </span>
          <span>
            <ExternalLink href="https://commission.europa.eu/law/law-topic/data-protection/international-dimension-data-protection_en">
              https://commission.europa.eu/law/law-topic/data-protection/international-dimension-data-protection_en
            </ExternalLink>{' '}
          </span>
          <span>. </span>
        </p>
        <p>
          <span>
            We take technical and organizational measures so that your data remains protected during
            the transfer to the applicable country. However, if you choose to shop at a store
            located in a country outside the EU/EEA, our data sharing with that foreign store, to
            administer your specific purchase, means that your personal data will be transferred to
            this country. You can always contact us at{' '}
          </span>
          <span>
            <a href="mailto:support@fishbrain.com">support@fishbrain.com</a>
          </span>
          <span>,</span>
          <span>&nbsp;if you want more information about our safety measures.</span>
        </p>
      </li>
      <li className="text-sm">
        <span>Complaints</span>
        <span>
          . If you are located in the EU/EEA/UK, you have the right to file a complaint with your
          local supervisory authority.{' '}
        </span>
        <span>You can find contact information for supervisory authorities in the EU/EEA at </span>
        <span>
          <ExternalLink href="https://ec.europa.eu/justice/article-29/structure/data-protection-authorities/index_en.htm">
            https://ec.europa.eu/justice/article-29/structure/data-protection-authorities/index_en.htm
          </ExternalLink>{' '}
        </span>
        <span>, and contact information for supervisory authorities in the UK at </span>
        <span>
          <ExternalLink href="https://ico.org.uk/make-a-complaint">
            https://ico.org.uk/make-a-complaint{' '}
          </ExternalLink>{' '}
        </span>
        <span>. </span>
      </li>
    </ol>
    <Heading variant="sm" className="mb-4">
      <span>14. Terms of Service</span>
    </Heading>
    <p>
      <span>
        If you choose to use the Fishbrain Services, your use of the Services, including access to{' '}
      </span>
      <span>
        <ExternalLink disabled={appView} href="https://fishbrain.com">
          https://fishbrain.com
        </ExternalLink>
      </span>
      <span>&nbsp;an</span>
      <span>
        d our app(s), and any dispute over privacy is subject to this Privacy Policy and our Terms
        of Service -{' '}
      </span>
      <span>
        <ExternalLink disabled={appView} href="https://fishbrain.com/terms-of-service">
          https://fishbrain.com/terms-of-service
        </ExternalLink>
      </span>
      <span>
        , including limitations on damages, resolution of disputes, and application of the law of
        the Country of Sweden, with respect to Fishbrain AB and the State of Delaware with respect
        to Fishbrain Inc.
      </span>
    </p>
    <Heading variant="sm" className="mb-4">
      <span>15. Revisions</span>
    </Heading>
    <p>
      <span>
        We may change this Privacy Policy at any time, effective upon posting or as otherwise
        required by applicable law. When we do make modifications to this Privacy Policy we will
        revise the date at the top of the Privacy Policy posted on our website and app. If you do
        not wish to accept such modified Privacy Policy, you can terminate your account. Any
        continued use by you of the Services following the posting or communication of modified
        Privacy Policy shall constitute your acceptance of such policy. We may email periodic
        reminders of our notices and conditions, but you should check{' '}
        <ExternalLink disabled={appView} href="https://fishbrain.com">
          www.fishbrain.com
        </ExternalLink>{' '}
        or the app frequently to see recent changes. We may also provide you with additional notice
        (such as adding a statement to our website&rsquo;s homepage or providing you with an in-app
        notification). The Fishbrain Terms of Service may be modified from time to time as provided
        therein.
      </span>
    </p>
    <p>
      <span>
        We stand behind the promises we make and will never materially change our Privacy Policy or
        practices to make them less protective of customer information collected in the past without
        any legally required consent of affected customers.
      </span>
    </p>
    <Heading variant="sm" className="mb-4">
      <span>16. Contact Us</span>
    </Heading>
    <p>
      <span>
        If you want to contact us about any of the matters in relation to this Privacy Policy please
        email{' '}
      </span>
      <span>
        <a href="mailto:support@fishbrain.com">support@fishbrain.com</a>
      </span>
      <span>
        &nbsp;with a thorough description and we will try to resolve it. If you have any complaints
        regarding the processing of your personal data, you can also contact the applicable
        supervisory authority.
      </span>
    </p>
    <Heading variant="sm" className="mb-4">
      <span>17. Acquisition of Fish Rules LLC</span>
    </Heading>
    <p>
      <span>
        In December 2021, Fishbrain acquired Fish Rules LLC. The processing of personal data between
        the parties has not been materially affected as a result of the acquisition. However,
        personal data relating to users who use or have used the services of Fish Rules LLC will be
        received by Fishbrain. In order to comply with applicable privacy laws, fulfill our
        contractual obligations and in line with our legitimate interest to conduct our business, we
        will process this personal data.
      </span>
    </p>
    <p>
      <span>
        The personal data received will be processed in line with applicable privacy laws and the
        privacy policy in force at the time of the acquisition. Please note that you also have the
        rights described in this privacy policy (please see Section 2). The privacy policy in force
        at the time of the acquisition, may be found at{' '}
      </span>
      <span>
        <ExternalLink href="https://www.iubenda.com/privacy-policy/93586157">
          https://www.iubenda.com/privacy-policy/93586157
        </ExternalLink>{' '}
      </span>
      <span>.</span>
    </p>
  </div>
);

// Needs default to auto load correct file

export default PrivacyPolicy;
